import Image from 'next/image';
import { Fragment } from 'react';

import { ArticleItemTeaserText } from '#components/Article/Items/ArticleItemTeaserText';
import { Link } from '#components/Link/Link';
import { SchemaOrg } from '#components/SchemaOrg';
import { buildAuthorName } from '#cutils/builders/buildAuthorName';
import { useEnvironment } from '#cutils/context/EnvironmentContext';
import { MediaContextPlaybackStatus } from '#cutils/context/MediaContext';
import { useDefaults } from '#cutils/hooks/useDefaults';
import { getIsMediaOffline } from '#cutils/media/getIsMediaOffline';
import { notNullOrUndefined } from '#cutils/misc/notNullOrUndefined';
import { getBreadcrumbSchema } from '#cutils/seo/schema-org/buildBreadcrumbStructuredData';
import { IconSystem } from '#icons/IconSystem';

import DefaultAuthorImage from '../../Author/default-author-image.svg';

import { ArticleModuleSourceOrigin } from './ArticleModuleSourceOrigin';
import styles from './ArticleModuleTeaser.module.scss';
import { getCategoryPath, getCategoryURL } from './ArticleModuleTeaser.utils';
import { ArticlePublicationDate } from './ArticlePublicationDate';
import type { ArticleModuleTeaserFragment } from './__generated__/ArticleModuleTeaser.generated';

type Props = {
	article: ArticleModuleTeaserFragment;
	heroMediaPlaybackState: MediaContextPlaybackStatus;
	className?: string;
};

// keep in sync with CSS in module
const authorImageDimension = 35;

export function ArticleModuleTeaser({ article, heroMediaPlaybackState, className = '' }: Props) {
	const { fallbackTeaserModule } = useDefaults();

	const [teaserModule = fallbackTeaserModule] = article.teaserModule.nodes.filter(notNullOrUndefined);
	const { meta } = teaserModule;

	const articleHeroType = teaserModule.type || null;

	const isPlayingOrPaused =
		heroMediaPlaybackState === MediaContextPlaybackStatus.PAUSED || heroMediaPlaybackState === MediaContextPlaybackStatus.STARTED;

	const isMediaOffline = getIsMediaOffline(meta);

	const articleAuthors = article.articlesAuthorsByArticleId.nodes
		.filter(notNullOrUndefined)
		.map((author) => author.authorByAuthorGuid)
		.filter(notNullOrUndefined);

	const category = article.categoryByPrimaryCategory?.title;

	const categoryURL = getCategoryURL(category || '');
	const categoryPath = getCategoryPath(category || '');
	const {
		constants: { appUrl },
	} = useEnvironment();

	const schema = getBreadcrumbSchema(article.title, category, categoryPath, appUrl);

	const authors = (articleAuthors ?? []).map((author) => {
		const authorName = buildAuthorName(author);

		const authorContent = (
			<>
				<Image
					className={styles.authorImage}
					width={authorImageDimension}
					height={authorImageDimension}
					src={author.profilePicture ? author.profilePicture : DefaultAuthorImage}
					alt=""
					unoptimized={!author.profilePicture}
				/>
				<span className={styles.authorName}>{authorName}</span>
			</>
		);

		return (
			<Fragment key={author.guid}>
				{author.isActive && author.path ? (
					<Link className={styles.author} href={`/autoren${author.path}`}>
						{authorContent}
					</Link>
				) : (
					<div className={styles.author}>{authorContent}</div>
				)}
			</Fragment>
		);
	});

	return (
		<section className={`${styles.wrapper} ${className} ${isPlayingOrPaused ? styles.playing : ''}`}>
			<section className={styles.meta}>
				<ArticlePublicationDate
					className={`${styles.publicationDate} body6`}
					publicationDate={article.firstPublicationDate}
					updatedAt={article.updatedAt}
					moduleType={!isMediaOffline ? articleHeroType : 'IMAGE'}
				/>
			</section>

			<div className={`${styles.breadcrumb} animate-hover underline-hover`}>
				<Link className={`${styles.homeIcon} outline-inside`} href="/">
					<IconSystem icon="home" variant="filled" title="Zur Startseite" />
				</Link>{' '}
				{categoryURL ? categoryURL : null} <span className={styles.breadcrumbArrow}>{'>'}</span>{' '}
				<h1 className={`body5 ${styles.breadcrumbHeadline}`}>{article.title}</h1>
				<SchemaOrg schema={schema} />
			</div>

			<h2 className={`heading1 ${styles.title}`}>{article.title}</h2>

			<ArticleItemTeaserText className="body5">{article.teaserText}</ArticleItemTeaserText>

			<div className={`${styles.authorSection} body5`}>
				<div className={styles.authorSectionVon}>Von</div>
				<div className={styles.authorSectionAuthors}>{authors}</div>
			</div>

			<ArticleModuleSourceOrigin className={styles.sourceOrigin} article={article} />
		</section>
	);
}
